import React, { useMemo, useState, useRef, useEffect } from 'react';
import { View, ScrollView, StyleSheet, useWindowDimensions, TouchableOpacity, PanResponder } from 'react-native';
import { useTheme } from '@react-navigation/native';
import CustomLabel from '../../../components/CustomLabel/CustomLabel';
import CustomInput from '../../../components/CustomInput';
import CustomButton from '../../../components/CustomButton';
import CustomDropDown from '../../../components/CustomDropDown/CustomDropdown';
import { useTranslation } from 'react-i18next';
import Ionicons from "@react-native-vector-icons/ionicons/static";
import CustomDropDownWithIcons from '../../../components/CustomDropDownWithIcons';
import Modal from "react-native-modal";
import { useAuthContext } from "../../../hooks/useAuthContext";

const TimesheetTable = ({
    data,
    isChargable,
    weekDates,
    jobCodeOptions,
    phaseCodeOptionsByIndex,
    setPhaseCodeOptionsByIndex,
    onEdit,
    onDelete,
    toView,
    setIsName,
    formatDateLabel,
    period,
    selectedOptions,
    setSelectedOptions,
    loadPhaseCodeForClient,
    isMonthlyTimesheet,
    startDate,
    endDate,
    tsInfo,
    isEmbedded,
    isLeftColumnCollapsed
}) => {
    //#region Const Definitions
    const { t } = useTranslation();
    const { width: screenWidth } = useWindowDimensions();
    const { user, clientApi, username } = useAuthContext();
    const { colors } = useTheme()
    const styles = makeStyles(colors);

    const [modalVisible, setModalVisible] = useState(false);
    const [modalOptions, setModalOptions] = useState([]);
    const [modalSelectedValues, setModalSelectedValues] = useState('');
    const [activeFilterColumn, setActiveFilterColumn] = useState(null); // 'name', 'jobno'
    const [activeRowIndex, setActiveRowIndex] = useState(null);
    const [activeRowItem, setActiveRowItem] = useState(null);
    const loadingOptions = [{ label: 'Loading...', value: 'loading', disabled: true }];

    const [initialColWidths, setInitialColWidths] = useState({});
    const [minColWidths, setMinColWidths] = useState({});
    const [colWidths, setColWidths] = useState({});
    const resizingCol = useRef(null);
    const panResponders = useRef({});
    //const availableWidth = screenWidth * (isEmbedded ? (isLeftColumnCollapsed ? 0.75 : 0.65) : 0.8);
    const availableWidth = useMemo(() => {
        return screenWidth * (isEmbedded ? (isLeftColumnCollapsed ? 0.75 : 0.65) : 0.8);
    }, [screenWidth, isEmbedded, isLeftColumnCollapsed]);
    const fixedDateColPercent = 0.06;
    const colWeights =  isMonthlyTimesheet ? [0.35, 0.25, 0.4] : [0.3, 0.2, 0.2, 0.3];

    useEffect(() => {
        if (weekDates) {
            const numDateCols = weekDates.length + 1;
            const totalDateColsPercent = fixedDateColPercent * numDateCols;
            const remainingPercent = 1 - totalDateColsPercent;
            const mainColWidths = colWeights.map(weight => availableWidth * remainingPercent * weight);
            const dayColWidth = availableWidth * fixedDateColPercent;

            // Compose initial widths object keyed by column names
            const newInitialColWidths = {
                description: mainColWidths[0],
                jobName: mainColWidths[1],
                jobCode: mainColWidths[2],
                phaseCode: mainColWidths[3],
            };
            weekDates.forEach((_, idx) => {
                newInitialColWidths[`d${idx}`] = dayColWidth;
            });
            newInitialColWidths.sum = dayColWidth;

            // Compose minimum widths similarly
            const newMinColWidths = {
                description: 50,
                jobName: 50,
                jobCode: 50,
                phaseCode: 50,
            };
            weekDates.forEach((_, idx) => {
                newMinColWidths[`d${idx}`] = dayColWidth;
            });
            newMinColWidths.sum = dayColWidth;

            setInitialColWidths(newInitialColWidths);
            setMinColWidths(newMinColWidths);
            setColWidths(newInitialColWidths);
        }
    }, [weekDates, availableWidth]);

    // Create PanResponder factory
    const createPanResponder = (colKey) =>
        PanResponder.create({
            onStartShouldSetPanResponder: () => true,
            onPanResponderGrant: () => {
                resizingCol.current = colKey;
            },
            onPanResponderMove: (evt, gestureState) => {
                if (!resizingCol.current) return;
                const deltaX = gestureState.dx;
                setColWidths((prev) => {
                    const colKeys = Object.keys(prev);
                    const index = colKeys.indexOf(resizingCol.current);
                    if (index === -1) return prev;

                    // Copy current widths
                    const newWidths = { ...prev };

                    // Proposed new width for resized column
                    let newWidth = prev[resizingCol.current] + deltaX;

                    // Clamp to min width for this column
                    const minWidth = minColWidths[resizingCol.current] ?? 50;
                    if (newWidth < minWidth) newWidth = minWidth;

                    // Width difference
                    let widthDiff = newWidth - prev[resizingCol.current];

                    // Set resized column width
                    newWidths[resizingCol.current] = newWidth;

                    // Adjust next columns to keep total width constant
                    let nextIndex = index + 1;
                    while (nextIndex < colKeys.length) {
                        const nextCol = colKeys[nextIndex];
                        const nextColWidth = newWidths[nextCol];
                        const nextColMinWidth = minColWidths[nextCol] ?? 50;
                        const nextColNewWidth = nextColWidth - widthDiff;

                        if (nextColNewWidth >= nextColMinWidth) {
                            newWidths[nextCol] = nextColNewWidth;
                            break;
                        } else {
                            // Shrink to min and reduce widthDiff accordingly
                            const diffUsed = nextColWidth - nextColMinWidth;
                            newWidths[nextCol] = nextColMinWidth;
                            widthDiff -= diffUsed;
                            nextIndex++;
                        }
                    }

                    // If no next column can shrink enough, revert resize
                    if (nextIndex === colKeys.length) {
                        return prev;
                    }

                    return newWidths;
                });
            },
            onPanResponderRelease: () => {
                resizingCol.current = null;
            },
        });

    // Create PanResponders for all columns
    Object.keys(initialColWidths).forEach(colKey => {
        panResponders[colKey] = createPanResponder(colKey);
    });
    //#endregion

    // Memoize options arrays for dropdowns
    const CodeOptions = useMemo(() => jobCodeOptions.map(({ value }) => ({ value, label: value })), [jobCodeOptions]);
    const JobNameOptions = useMemo(() => jobCodeOptions.map(({ label }) => ({ value: label, label })), [jobCodeOptions]);

    // Get first day for timesheet base don period
    const datesIndexForDate = (date) => {
        const day = date.getDate();
        if (period === 'A') return day;
        if (period === 'B') return day - 15;
        if (period === 'W') {
            // For weekly: use day of week (1=Monday, 7=Sunday)
            const dayOfWeek = date.getDay();
            return dayOfWeek === 0 ? 7 : dayOfWeek;
        }
        return day;
    };

    const getMonthlyDayKey = (date) => {
        const dateDay = date.getDate();
        const startDay = parseInt(startDate) || 21;
        const endDay = parseInt(endDate) || 20;
        const daysInMonth = new Date(tsInfo[0].periodA[0].year, tsInfo[0].periodA[0].month, 0).getDate();
        
        const periodAEndDay = startDay + 15;
        if (dateDay >= startDay) {
            // Period A: startDay onwards in current month
            return `d${dateDay - startDay + 1}`;
        } else if (dateDay < 16-(daysInMonth - startDay)) {
            // Period A: 1st until 16 days of period are done (in next month)
            return `d${dateDay + (daysInMonth - startDay) + 1}`;
        } else {
            // Period B: days in next month (until endDay)
            // Calculate which day of Period B this is
            const dayInPeriodB = dateDay - (16 - (daysInMonth - startDay)) + 1;
            return `d${dayInPeriodB}`;
        }
    };
    //#endregion

    const openFilterModal = (column, index, item,) => {
        setActiveFilterColumn(column);
        setActiveRowIndex(index);
        setActiveRowItem(item);

        switch (column) {
            case 'name':
                setModalOptions(JobNameOptions);
                break;
            case 'jobno':
                setModalOptions(CodeOptions);
                break;
            default:
                setModalOptions([]);
        }
        setModalVisible(true);
    };

    return (
        <View style={styles.container}>
            {/* Header Row */}
            <View style={[styles.row, styles.headerRow]}>
                {/* Main columns */}
                <View style={[styles.cell, { width: colWidths.description, alignItems: 'center', position: 'relative', pointerEvents: 'box-none' }]}>
                    <CustomLabel value={t('common:description')} type="MAJOR" />
                    <View {...panResponders.description?.panHandlers} style={[styles.resizer, { pointerEvents: 'auto' }]} />
                </View>
                <View style={[styles.cell, { width: colWidths.jobName, alignItems: 'center', position: 'relative', pointerEvents: 'box-none' }]}>
                    <CustomLabel value={t('common:jobName')} type="MAJOR" />
                    <View {...panResponders.jobName?.panHandlers} style={[styles.resizer, { pointerEvents: 'auto' }]} />
                </View>
                <View style={[styles.cell, { width: colWidths.jobCode, alignItems: 'center', position: 'relative', pointerEvents: 'box-none' }]}>
                    <CustomLabel value={t('common:jobCode')} type="MAJOR" />
                    <View {...panResponders.jobCode?.panHandlers} style={[styles.resizer, { pointerEvents: 'auto' }]} />
                </View>
                {!isMonthlyTimesheet && <View style={[styles.cell, { width: colWidths.phaseCode, alignItems: 'center', position: 'relative', pointerEvents: 'box-none' }]}>
                    <CustomLabel value={t('common:phaseCode')} type="MAJOR" />
                    <View {...panResponders.phaseCode?.panHandlers} style={[styles.resizer, { pointerEvents: 'auto' }]} />
                </View>}

                {/* Day columns */}
                {weekDates.map((date, idx) => {
                    const colKey = `d${idx}`;
                    return (
                        <View key={idx} style={[styles.cell, { width: colWidths[colKey], alignItems: 'center', position: 'relative', pointerEvents: 'box-none' }]}>
                            <CustomLabel value={formatDateLabel(date)} type="MAJOR" />
                            <View {...panResponders[colKey]?.panHandlers} style={[styles.resizer, { pointerEvents: 'auto' }]} />
                        </View>
                    );
                })}

                {/* Sum column */}
                <View style={[styles.cell, { width: colWidths.sum, alignItems: 'center', position: 'relative' }]}>
                    <CustomLabel value={t('common:sum')} type="MAJOR" />
                </View>
            </View>

            <ScrollView 
                key={`scroll-${data?.length}-${weekDates?.length}`}
                style={{ maxHeight: isEmbedded ? 350 : 550 }}
            >
                {/* Data Rows */}
                {data && Array.isArray(data) && data.map((item, index) => {
                    // Calculate the sum for the week
                    const sum = weekDates.reduce((acc, date) => {
                        const dayKey = `d${datesIndexForDate(date)}`;
                        const val = parseInt(item[dayKey], 10);
                        return acc + (isNaN(val) ? 0 : val);
                    }, 0);

                    return (
                        <View key={index} style={styles.row}>
                            {/* Main columns */}
                            <View style={[styles.cell, { width: colWidths.description, alignItems: 'center', backgroundColor: '#f2f2f2' }]}>
                                <CustomInput
                                    editable={!toView}
                                    value={item.description}
                                    setValue={(val) => onEdit(index, item, 'description', val, isMonthlyTimesheet && period)}
                                    maxLen={150}
                                    type="TIMESHEETS"
                                />
                            </View>
                            <View style={[styles.cell_dropdown, { width: colWidths.jobName, alignItems: 'center' }]}>
                                <CustomDropDown
                                    disabled={toView}
                                    defValue={item.name}
                                    options={
                                        toView
                                            ? JobNameOptions.filter(opt => opt.label === item.name)
                                            : JobNameOptions.filter(opt => selectedOptions.name.includes(opt.value))
                                    }
                                    value={item.name}
                                    setValue={(val) => {
                                        onEdit(index, item, 'name', val, isMonthlyTimesheet && period);
                                        setIsName(true);
                                    }}
                                    type='RESULTS'
                                />
                                {!toView && (
                                    <TouchableOpacity onPress={() => openFilterModal('name', index, item)} style={{ marginLeft: 5 }}>
                                        <Ionicons name="search-outline" size={20} color={colors.text} />
                                    </TouchableOpacity>
                                )}
                            </View>
                            <View style={[styles.cell_dropdown, { width: colWidths.jobCode, alignItems: 'center', backgroundColor: '#f2f2f2' }]}>
                                <CustomDropDown
                                    disabled={toView}
                                    defValue={item.jobno}
                                    options={
                                        toView ? [{ value: item.jobno, label: item.jobno }] :
                                            CodeOptions.filter(opt => selectedOptions.jobno.includes(opt.value))
                                    }
                                    value={item.jobno}
                                    setValue={(val) => {
                                        onEdit(index, item, 'jobno', val, isMonthlyTimesheet && period);
                                        loadPhaseCodeForClient( val).then((resp) => {
                                            if (isChargable)
                                                setPhaseCodeOptionsByIndex(prev => {
                                                    const newArr = [...prev];
                                                    newArr[index] = resp.UsersChargPhaseCode || [];
                                                    return newArr;
                                                });
                                            else setPhaseCodeOptionsByIndex(prev => {
                                                const newArr = [...prev];
                                                newArr[index] = resp.UsersNonChargPhaseCode || [];
                                                return newArr;
                                            });
                                        });
                                        setIsName(false);
                                    }}
                                    type='RESULTS'
                                />
                                {!toView && (
                                    <TouchableOpacity onPress={() => openFilterModal('jobno', index, item)} style={{ marginLeft: 5 }}>
                                        <Ionicons name="search-outline" size={20} color={colors.text} />
                                    </TouchableOpacity>
                                )}
                            </View>
                            {!isMonthlyTimesheet && <View style={[styles.cell_dropdown, { width: colWidths.phaseCode, alignItems: 'center', zIndex: 100000 / (index + 1) }]}>
                                <CustomDropDown
                                    disabled={toView}
                                    defValue={item.phasecode}
                                    options={
                                        toView
                                            ? [{ value: item.phasecode, label: item.phasecode }]
                                            : phaseCodeOptionsByIndex[index] || []
                                    }
                                    value={item.phasecode}
                                    setValue={(val) => onEdit(index, item, 'phasecode', val, isMonthlyTimesheet && period)}
                                    type='RESULTS'
                                />
                            </View>}

                            {/* Day columns */}
                            {weekDates?.map((date, idx) => {
                                const colKey = `d${idx}`;

                                // Calculate the day label based on the date and period
                                let dayKey;
                                if (isMonthlyTimesheet) {
                                    dayKey = getMonthlyDayKey(date);
                                } else {
                                    // Regular timesheet: use the original logic
                                    dayKey = `d${datesIndexForDate(date)}`;
                                }

                                return (
                                    <View
                                        key={idx}
                                        style={[
                                            styles.cell,
                                            {
                                                width: colWidths[colKey],
                                                alignItems: 'center',
                                                backgroundColor: (idx % 2) === 0 ? '#f2f2f2' : colors.background_card,
                                            }
                                        ]}
                                    >
                                        <CustomInput
                                            editable={!toView}
                                            value={item[dayKey]?.toString() ?? ''}
                                            setValue={(val) => {
                                                const cleaned = val
                                                .replace(',', '.')
                                                .replace(/[^0-9.]/g, '')
                                                .replace(/(\..*)\./g, '$1'); 

                                                onEdit(index, item, dayKey, cleaned, isMonthlyTimesheet && period);
                                            }}
                                            keyboardType="decimal-pad"
                                            maxLen={6}
                                            type="TIMESHEETS"
                                        />
                                    </View>
                                );
                            })}
                            {/* Sum column */}
                            <View style={[
                                styles.cell_last,
                                {
                                    width: colWidths.sum,
                                    alignItems: 'center',
                                    backgroundColor: colors.background_card,
                                    flexDirection: 'row',
                                }
                            ]}>
                                <CustomLabel value={sum.toString()} type="MAJOR" />
                                {!toView && onDelete && (
                                    <TouchableOpacity onPress={() => onDelete(item, index, 'rowguid')} style={{ marginLeft: 8 }}>
                                        <Ionicons name="trash-outline" size={20} color={colors.text} />
                                    </TouchableOpacity>
                                )}
                            </View>
                        </View>
                    );
                })}
            </ScrollView>
            <Modal
                isVisible={modalVisible}
                hasBackdrop={true}
                backdropColor={colors.background_approved}
                backdropOpacity={0.9}
                onBackdropPress={() => setModalVisible(false)}
                onRequestClose={() => setModalVisible(false)}
            >
                <View style={{ width: '50%', height: '40%', alignSelf: 'center', backgroundColor: 'white', padding: 20, borderRadius: 10, zIndex: 10000 }}>
                    <View style={{ alignSelf: 'center', margin: 20 }}>
                        <CustomLabel value={"All Options"} type="TITLE" />
                    </View>
                    <CustomDropDownWithIcons
                        options={modalOptions}
                        value={modalSelectedValues}
                        defValue={modalSelectedValues}
                        setValue={setModalSelectedValues}
                        type="PARTICIPANT"
                        loading={true}
                    />
                    <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginTop: 30, zIndex: -1 }}>
                        <CustomButton
                            text={t('common:apply')}
                            onPress={() => {
                                onEdit(activeRowIndex, activeRowItem, activeFilterColumn, modalSelectedValues, isMonthlyTimesheet && period)
                                setSelectedOptions(prev => {
                                    const nameArr = prev.name || [];
                                    const jobnoArr = prev.jobno || [];
                                    const phasecodeArr = prev.phasecode || [];

                                    if (activeFilterColumn === 'name') {
                                        return {
                                            ...prev,
                                            name: [...new Set([...nameArr, modalSelectedValues])],
                                            jobno: jobnoArr,
                                            phasecode: phasecodeArr,
                                        };
                                    } else if (activeFilterColumn === 'jobno') {
                                        return {
                                            ...prev,
                                            name: nameArr,
                                            jobno: [...new Set([...jobnoArr, modalSelectedValues])],
                                            phasecode: phasecodeArr,
                                        };
                                    } else {
                                        return prev;
                                    }
                                });
                                setModalVisible(false)
                            }}
                            type="BUTTON_SAVE"
                            sz={15}
                            isGradient={true}
                        />
                        <CustomButton
                            text={t('common:cancel')}
                            onPress={() => {
                                setModalSelectedValues('');
                                setModalVisible(false);
                            }}
                            type="BUTTON_CANCEL"
                            sz={15}
                            style={{ marginLeft: 10 }}
                        />
                    </View>
                </View>
            </Modal>
        </View>
    );
};

const makeStyles = (colors) => StyleSheet.create({
    container: {
        flexDirection: 'column',
        borderWidth: 1,
        borderColor: '#ddd',
    },
    row: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#ddd',
        alignItems: 'center',
    },
    headerRow: {
        backgroundColor: '#dcdcdc',
    },
    cell: {
        padding: 8,
        borderRightWidth: 1,
        borderRightColor: '#ddd',
        justifyContent: 'center',
        borderRightHeight: '100%'
    },
    cell_last: {
        padding: 8,
        justifyContent: 'center',
    },
    cell_dropdown: {
        padding: 8,
        borderRightWidth: 1,
        borderRightColor: '#ddd',
        justifyContent: 'center',
        borderRightHeight: '100%',
        flexDirection: 'row',
        paddingTop: 22.5,
        paddingBottom: 22.5
    },
    resizer: {
        width: 20,
        height: '100%',
        position: 'absolute',
        right: 0,
        top: 0,
        zIndex: 10,
        borderRightColor: '#666',
        borderRightWidth: 1,
        cursor: 'e-resize',
    },
});

export default TimesheetTable;